import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

// Role permissions mapping
export const ROLE_PERMISSIONS = {
  admin: {
    canView: ['all'],
    canEdit: ['all'],
    canDelete: ['all'],
    canCreate: ['all'],
    canExport: true,
    canManageStaff: true,
    canManageBilling: true,
    canManageResidents: true,
    canViewReports: true,
    canManageSettings: true,
  },
  nurse_manager: {
    canView: ['all'],
    canEdit: ['residents', 'medications', 'assessments', 'care_plans', 'staff', 'billing', 'documents'],
    canDelete: ['residents', 'medications', 'assessments', 'documents'],
    canCreate: ['residents', 'medications', 'assessments', 'care_plans', 'staff', 'billing', 'documents'],
    canExport: true,
    canManageStaff: true,
    canManageBilling: true,
    canManageResidents: true,
    canViewReports: true,
    canManageSettings: false,
  },
  nurse: {
    canView: ['all'],
    canEdit: ['residents', 'medications', 'assessments', 'care_plans'],
    canDelete: [],
    canCreate: ['medications', 'assessments', 'care_plans'],
    canExport: false,
    canManageStaff: false,
    canManageBilling: false,
    canManageResidents: false,
    canViewReports: true,
    canManageSettings: false,
  },
  caregiver: {
    canView: ['residents', 'medications', 'timeclock'],
    canEdit: ['medications'],
    canDelete: [],
    canCreate: [],
    canExport: false,
    canManageStaff: false,
    canManageBilling: false,
    canManageResidents: false,
    canViewReports: false,
    canManageSettings: false,
  },
  staff: {
    canView: ['residents', 'timeclock'],
    canEdit: [],
    canDelete: [],
    canCreate: [],
    canExport: false,
    canManageStaff: false,
    canManageBilling: false,
    canManageResidents: false,
    canViewReports: false,
    canManageSettings: false,
  },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const hasPermission = (action, resource = null) => {
    if (!user) return false;
    const permissions = ROLE_PERMISSIONS[user.role] || ROLE_PERMISSIONS.staff;
    
    if (action === 'view') {
      return permissions.canView.includes('all') || (resource && permissions.canView.includes(resource));
    }
    if (action === 'edit') {
      return permissions.canEdit.includes('all') || (resource && permissions.canEdit.includes(resource));
    }
    if (action === 'delete') {
      return permissions.canDelete.includes('all') || (resource && permissions.canDelete.includes(resource));
    }
    if (action === 'create') {
      return permissions.canCreate.includes('all') || (resource && permissions.canCreate.includes(resource));
    }
    if (action === 'export') {
      return permissions.canExport;
    }
    if (action === 'manageStaff') {
      return permissions.canManageStaff;
    }
    if (action === 'manageBilling') {
      return permissions.canManageBilling;
    }
    if (action === 'manageResidents') {
      return permissions.canManageResidents;
    }
    if (action === 'viewReports') {
      return permissions.canViewReports;
    }
    if (action === 'manageSettings') {
      return permissions.canManageSettings;
    }
    return false;
  };

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { token, user } = res.data;
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      setUser(user);
      return { success: true, user };
    } catch (err) {
      return { success: false, error: err.response?.data?.message || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      try {
        const res = await api.get('/auth/me');
        setUser(res.data.user);
        setToken(storedToken);
      } catch (err) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
        setToken(null);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading, 
      token,
      hasPermission,
      role: user?.role,
      isAdmin: user?.role === 'admin',
      isNurseManager: user?.role === 'nurse_manager',
      isNurse: user?.role === 'nurse',
      isCaregiver: user?.role === 'caregiver',
      isStaff: user?.role === 'staff',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}